import { colors } from '@kmns-internal/furikake'
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import {
  type ScheduleListInRangeItem,
  type ScheduleListInRangeItemInfo,
  ScheduleTypeEnum,
} from '@ulysses-inc/harami_api_client'
import {
  addMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  getDate,
  getDay,
  getMonth,
  isAfter,
  startOfMonth,
  subMonths,
} from 'date-fns'
import type React from 'react'
import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import dateUtil from 'src/exShared/util/date'
import {
  ScheduleStatus,
  getScheduleStatus,
} from 'src/exShared/util/scheduleListInRange/convertScheduleListInRange'
import scheduleListInRangeItemsOperations from 'src/state/ducks/scheduleListInRangeItems/operations'
import type { RootState } from 'src/state/store'

interface OwnProps {
  placeNodeUUID: string
  targetDate: Date
  changeTargetDate: (targetDate: Date) => void
}

interface StateProps {
  scheduleListInRangeItems: ScheduleListInRangeItem[]
  isLoading: boolean
}

interface DispatchProps {
  getMonthlyPlaceScheduleListInRangeItems: (
    placeNodeUUID: string,
    startDate: Date,
    endDate: Date,
  ) => void
}

type PlaceCalendarProps = OwnProps & StateProps & DispatchProps

const useStateProps = (): StateProps => {
  return {
    scheduleListInRangeItems: useSelector(
      (state: RootState) =>
        state.scheduleListInRangeItemsState.monthlyPlaceScheduleListInRangeItems
          .scheduleListInRangeItems,
      shallowEqual,
    ),
    isLoading: useSelector(
      (state: RootState) =>
        state.scheduleListInRangeItemsState.monthlyPlaceScheduleListInRangeItems
          .isLoading,
      shallowEqual,
    ),
  }
}

const useReducerProps = (): DispatchProps => {
  const dispatch = useDispatch()

  return {
    getMonthlyPlaceScheduleListInRangeItems: (
      placeNodeUUID: string,
      startDate: Date,
      endDate: Date,
    ) => {
      scheduleListInRangeItemsOperations.getMonthlyPlaceScheduleListInRangeItems(
        dispatch,
        {
          placeNodeId: { $in: [placeNodeUUID] },
          startDate: startDate,
          endDate: endDate,
        },
        'Asia/Tokyo',
      )
    },
  }
}

const PlaceCalendar: React.FC<PlaceCalendarProps> = (
  props: PlaceCalendarProps,
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [targetMonth, setTargetMonth] = useState<Date>(props.targetDate)
  const [alerts, setAlerts] = useState<{ [date: string]: boolean }>({})

  useEffect(() => {
    const targetMonth = startOfMonth(props.targetDate)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setTargetMonth(targetMonth)
    props.getMonthlyPlaceScheduleListInRangeItems(
      props.placeNodeUUID,
      targetMonth,
      endOfMonth(targetMonth),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.targetDate])

  useEffect(() => {
    const today = dateUtil.formatYYYYMMDD_hyphen()
    const alerts: { [date: string]: boolean } = {}
    props.scheduleListInRangeItems.forEach((item: ScheduleListInRangeItem) => {
      if (!item?.infos || !item.targetDate) {
        return
      }
      if (today < item.targetDate) {
        return
      }

      const alertReports = item.infos
        .filter(info => {
          if (
            info.scheduleType === ScheduleTypeEnum.Duration &&
            info.scheduleInDuration?.through
          ) {
            // 期間スケジュールの場合、期間の最終日のみチェック対象
            const formattedThrough = dateUtil.formatYYYYMMDD_hyphen_locale(
              info.scheduleInDuration.through,
            )
            return formattedThrough === item.targetDate
          }
          return true
        })
        .find((info: ScheduleListInRangeItemInfo) => {
          const scheduleStatus = getScheduleStatus(info)
          return scheduleStatus === ScheduleStatus.INCOMPLETE
        })

      alerts[item.targetDate] = !!alertReports
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setAlerts(alerts)
  }, [props.scheduleListInRangeItems])

  const getCalendarArray = () => {
    const sundays = eachWeekOfInterval({
      start: startOfMonth(targetMonth),
      end: endOfMonth(targetMonth),
    })
    return sundays.map(sunday =>
      eachDayOfInterval({ start: sunday, end: endOfWeek(sunday) }),
    )
  }

  const renderCalendar = () => {
    return (
      <Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-around"
          mb={1}
        >
          <IconButton
            color="primary"
            onClick={() => {
              const newTargetMonth = subMonths(targetMonth, 1)
              setTargetMonth(newTargetMonth)
              props.getMonthlyPlaceScheduleListInRangeItems(
                props.placeNodeUUID,
                newTargetMonth,
                endOfMonth(newTargetMonth),
              )
            }}
          >
            <ChevronLeft
              sx={{ color: colors.primary.main }}
              aria-label="前の月"
            />
          </IconButton>
          <Typography variant="body2" fontWeight="bold">
            {dateUtil.formatYYYYM_JP(targetMonth)}
          </Typography>
          <IconButton
            color="primary"
            onClick={() => {
              const newTargetMonth = addMonths(targetMonth, 1)
              setTargetMonth(newTargetMonth)
              props.getMonthlyPlaceScheduleListInRangeItems(
                props.placeNodeUUID,
                newTargetMonth,
                endOfMonth(newTargetMonth),
              )
            }}
          >
            <ChevronRight
              sx={{ color: colors.primary.main }}
              aria-label="次の月"
            />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            minWidth: 280,
            minHeight: 220,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          {props.isLoading && <CircularProgress />}
          {!props.isLoading && (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.5}>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  日
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  月
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  火
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  水
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  木
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  金
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ width: 32, textAlign: 'center', fontWeight: 'bold' }}
                >
                  土
                </Typography>
              </Stack>
              {getCalendarArray().map((weekRow, rowNum) => (
                <Stack direction="row" spacing={0.5} key={rowNum}>
                  {weekRow.map(currentDate => {
                    const formattedDate =
                      dateUtil.formatYYYYMMDD_hyphen(currentDate)

                    if (
                      getMonth(currentDate) !== getMonth(targetMonth) ||
                      isAfter(currentDate, dateUtil.today())
                    ) {
                      return (
                        <Stack
                          sx={{
                            color: colors.text.disabled,
                            width: 32,
                            textAlign: 'center',
                            justifyContent: 'center',
                          }}
                          key={getDay(currentDate)}
                        >
                          <Typography variant="caption">
                            {getDate(currentDate)}
                          </Typography>
                        </Stack>
                      )
                    }
                    if (alerts[formattedDate]) {
                      return (
                        <Box
                          key={getDay(currentDate)}
                          sx={{ width: 32, textAlign: 'center' }}
                        >
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              width: '100%',
                              minWidth: 'auto',
                              backgroundColor: colors.error.selected,
                              p: 0,
                              '&:hover': {
                                backgroundColor: colors.error.hover,
                              },
                            }}
                            onClick={() => {
                              props.changeTargetDate(currentDate)
                              setAnchorEl(null)
                            }}
                          >
                            {getDate(currentDate)}
                          </Button>
                        </Box>
                      )
                    }
                    return (
                      <Box
                        key={getDay(currentDate)}
                        sx={{ width: 32, textAlign: 'center' }}
                      >
                        <Button
                          variant="text"
                          color="secondary"
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            width: '100%',
                            minWidth: 'auto',
                            p: 0,
                          }}
                          onClick={() => {
                            props.changeTargetDate(currentDate)
                            setAnchorEl(null)
                          }}
                        >
                          {getDate(currentDate)}
                        </Button>
                      </Box>
                    )
                  })}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        <Button
          variant="text"
          sx={{ width: '100%' }}
          size="small"
          color="primary"
          onClick={() => {
            const newTargetMonth = startOfMonth(dateUtil.today())
            setTargetMonth(newTargetMonth)
            props.getMonthlyPlaceScheduleListInRangeItems(
              props.placeNodeUUID,
              newTargetMonth,
              endOfMonth(newTargetMonth),
            )
          }}
        >
          今月
        </Button>
      </Stack>
    )
  }

  const groundArea = (
    <Button
      variant="text"
      color="primary"
      size="small"
      startIcon={<CalendarToday style={{ color: colors.primary.main }} />}
      endIcon={<ExpandMore style={{ color: colors.primary.main }} />}
      onClick={e => setAnchorEl(e.currentTarget)}
    >
      <Typography
        variant="body2"
        sx={{ color: colors.text.primary, fontWeight: 'bold' }}
      >
        {dateUtil.formatMD_JP(props.targetDate)}
      </Typography>
    </Button>
  )

  return (
    <>
      {groundArea}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>{renderCalendar()}</Box>
      </Popover>
    </>
  )
}

const PlaceDashboardContainer: React.FC<OwnProps> = (ownProps: OwnProps) => {
  const stateProps = useStateProps()
  const dispatchProps = useReducerProps()
  const props = { ...stateProps, ...dispatchProps, ...ownProps }
  return <PlaceCalendar {...props} />
}

export default PlaceDashboardContainer
